// Вход через Authentik (OIDC Authorization Code + PKCE) и анонимный режим.
//
// Настройки входа отдаёт бэкенд (GET /api/v1/auth/config): режим, issuer,
// client_id, scopes. Один и тот же бандл работает с любым окружением; при
// AUTH_MODE=disabled (или старом бэкенде без /auth/config) вход не включается
// и запросы уходят как раньше — без заголовков авторизации.
//
// AUTH_MODE=required:
//   - вошедший: access token Authentik в заголовке Authorization у /api/v1,
//     /gis/v1 и /gis/tiles; тихое продление по refresh_token; 401 → одна
//     попытка обновить токен, иначе переход в анонимный режим;
//   - аноним: только общие дашборды; пропуск ГИС (X-CDO-Geo-Ticket) выдаёт
//     бэкенд под открытый общий дашборд и обновляется до истечения.
//
// Токены — в памяти и sessionStorage (живут до закрытия вкладки), не в
// localStorage: так их не унаследуют другие вкладки и не прочитает следующий
// пользователь общего компьютера после закрытия браузера.

import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';

import { api } from './api';
import { ApiError, setRequestAuth } from './http';
import type { AuthConfig, AuthMe, GeoTicket } from './types';
import { tr } from './ui-i18n';

export const AUTH_CALLBACK_PATH = '/auth/callback';
// Ответ Authentik (discovery, токен) дольше — ошибка, а не бесконечное ожидание.
const AUTH_REQUEST_TIMEOUT_SECONDS = 10;

export const PERM_DASHBOARDS_OWN = 'dashboards:own';
export const PERM_DASHBOARDS_EDIT_PUBLIC = 'dashboards:edit-public';
export const PERM_DASHBOARDS_MANAGE_ALL = 'dashboards:manage-all';
export const PERM_CATALOG_READ = 'catalog:read';

/** Повторная выдача пропуска — за столько секунд до истечения. */
const GEO_TICKET_REFRESH_MARGIN_SECONDS = 60;

export type AuthStatus = 'disabled' | 'anonymous' | 'signed-in';

export interface AuthState {
  status: AuthStatus;
  config: AuthConfig | null;
  user: User | null;
  me: AuthMe | null;
  /** Почему вход потерян (истёк, отозван) — для плашки; null — не терялся. */
  lostReason: string | null;
  /** Ошибка возврата с Authentik — для статусной строки. */
  callbackError: string | null;
}

const authState: AuthState = {
  status: 'disabled',
  config: null,
  user: null,
  me: null,
  lostReason: null,
  callbackError: null,
};

let manager: UserManager | null = null;
let refreshInFlight: Promise<boolean> | null = null;
const listeners = new Set<(state: AuthState) => void>();

let geoTicket: { value: string; header: string; dashboardId: string | null; expiresAt: number } | null = null;
let geoTicketTimer: number | null = null;
let geoTicketDashboardId: string | null = null;

export function auth(): Readonly<AuthState> {
  return authState;
}

/** Вход включён на бэкенде (AUTH_MODE=required). */
export function authRequired(): boolean {
  return authState.status !== 'disabled';
}

/** Вход включён, а пользователь не вошёл: только общие дашборды. */
export function isAnonymous(): boolean {
  return authState.status === 'anonymous';
}

export function isSignedIn(): boolean {
  return authState.status === 'signed-in';
}

/**
 * Есть ли у текущего посетителя право. Без входа (disabled) — да: прежнее
 * поведение, бэкенд считает каждого пользователем `local` с полными правами.
 */
export function hasPermission(permission: string): boolean {
  if (authState.status === 'disabled') return true;
  if (authState.status === 'anonymous') return authState.config?.anonymous_permissions.includes(permission) ?? false;
  if (authState.me) return authState.me.permissions.includes(permission);
  return false;
}

export function onAuthChange(listener: (state: AuthState) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit(): void {
  listeners.forEach((listener) => listener(authState));
}

export function userDisplayName(): string {
  const profile = authState.user?.profile;
  return (profile?.name as string | undefined)
    || authState.me?.username
    || (profile?.preferred_username as string | undefined)
    || (profile?.email as string | undefined)
    || authState.me?.subject
    || '';
}

/** Главная роль: admin > editor > viewer > api. */
export function primaryRole(): string | null {
  const roles = authState.me?.roles ?? [];
  return ['admin', 'editor', 'viewer', 'api'].find((role) => roles.includes(role)) ?? roles[0] ?? null;
}

// --- Настройки ---------------------------------------------------------------

async function fetchConfig(): Promise<AuthConfig | null> {
  try {
    const response = await fetch('/api/v1/auth/config', { headers: { Accept: 'application/json' } });
    // Старый бэкенд без модели доступа (404) — вход выключен, как раньше.
    if (!response.ok || !(response.headers.get('content-type') ?? '').includes('json')) return null;
    return (await response.json()) as AuthConfig;
  } catch {
    return null;
  }
}

function clientIdFor(config: AuthConfig): string | null {
  // client_id приходит из бэкенда (OIDC_AUDIENCE = client_id Authentik);
  // переменная сборки — запасной путь для бэкенда, который его не отдаёт.
  return config.client_id || import.meta.env.VITE_OIDC_CLIENT_ID || null;
}

function scopesFor(config: AuthConfig): string {
  const scopes = new Set(config.scopes.length ? config.scopes : ['openid', 'profile', 'email']);
  scopes.add('openid');
  // Без offline_access Authentik не выдаёт refresh_token, и вход истекал бы
  // вместе с access token (15 минут).
  scopes.add('offline_access');
  return [...scopes].join(' ');
}

function createManager(config: AuthConfig, clientId: string): UserManager {
  const origin = window.location.origin;
  const store = new WebStorageStateStore({ store: window.sessionStorage, prefix: 'cdo.oidc.' });
  // Отдельный префикс: clearStaleState() перебирает только записи state, не сохранённый вход.
  const stateStore = new WebStorageStateStore({ store: window.sessionStorage, prefix: 'cdo.oidc.state.' });
  return new UserManager({
    authority: config.issuer ?? '',
    client_id: clientId,
    redirect_uri: `${origin}${AUTH_CALLBACK_PATH}`,
    post_logout_redirect_uri: `${origin}/`,
    response_type: 'code',
    scope: scopesFor(config),
    userStore: store,
    stateStore,
    // Без таймаута запрос к Authentik (discovery, токен) мог висеть сколько угодно,
    // и кнопка «Войти» выглядела неработающей.
    requestTimeoutInSeconds: AUTH_REQUEST_TIMEOUT_SECONDS,
    // Продление — только refresh_token (см. renew()): тихий вход в iframe
    // требует отдельного redirect URI и сторонних cookie.
    automaticSilentRenew: false,
    // Имя и почта есть в id_token; userinfo — лишний запрос к Authentik.
    loadUserInfo: false,
    monitorSession: false,
  });
}

// --- Запуск --------------------------------------------------------------------

/**
 * Узнать режим входа, завершить возврат с Authentik (если это он) и
 * восстановить вход из sessionStorage. Вызывается до первой загрузки данных.
 */
export async function initAuth(): Promise<void> {
  const config = await fetchConfig();
  authState.config = config;
  if (!config || config.mode !== 'required') {
    authState.status = 'disabled';
    if (window.location.pathname === AUTH_CALLBACK_PATH) window.history.replaceState(null, '', '/');
    return;
  }
  setRequestAuth({ headers: requestHeaders, refresh: () => renew() });
  authState.status = 'anonymous';
  const clientId = clientIdFor(config);
  if (!config.issuer || !clientId) {
    // Вход настроен на бэкенде не полностью — остаётся анонимный просмотр.
    authState.callbackError = 'issuer/client_id';
    if (window.location.pathname === AUTH_CALLBACK_PATH) window.history.replaceState(null, '', '/');
    return;
  }
  manager = createManager(config, clientId);
  manager.events.addAccessTokenExpiring(() => void renew());
  // Discovery заранее: к нажатию «Войти» метаданные уже в кэше менеджера.
  void manager.metadataService.getMetadata().catch(() => undefined);

  if (window.location.pathname === AUTH_CALLBACK_PATH) {
    let returnTo = '/';
    try {
      const user = await manager.signinRedirectCallback();
      returnTo = safeReturnPath((user.state as { returnTo?: string } | undefined)?.returnTo);
    } catch (error) {
      authState.callbackError = error instanceof Error ? error.message : String(error);
    }
    window.history.replaceState(null, '', returnTo);
  }

  const user = await manager.getUser();
  if (user && !user.expired) await adoptUser(user);
  else if (user?.refresh_token) await renew();
  else if (user) await manager.removeUser();
}

function safeReturnPath(value: string | undefined): string {
  // Только путь своего приложения: открытый редирект через state недопустим.
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith(AUTH_CALLBACK_PATH)) return '/';
  return value;
}

async function adoptUser(user: User): Promise<void> {
  authState.user = user;
  authState.status = 'signed-in';
  authState.lostReason = null;
  clearGeoTicket();
  try {
    authState.me = await requestMe(user);
  } catch (error) {
    // Токен Authentik бэкенд не принял (чужой issuer/audience) — без входа.
    if (error instanceof ApiError && error.status === 401) {
      await dropUser('rejected');
      return;
    }
    authState.me = null;
  }
}

async function requestMe(user: User): Promise<AuthMe> {
  const response = await fetch('/api/v1/auth/me', {
    headers: { Accept: 'application/json', Authorization: `Bearer ${user.access_token}` },
  });
  if (!response.ok) throw new ApiError(response.status, response.statusText);
  return (await response.json()) as AuthMe;
}

async function dropUser(reason: string): Promise<void> {
  authState.user = null;
  authState.me = null;
  authState.status = 'anonymous';
  authState.lostReason = reason;
  try {
    await manager?.removeUser();
  } catch {}
}

/**
 * Обновить access token по refresh_token. Параллельные 401 ждут одной
 * попытки. Неудача — переход в анонимный режим с предложением войти.
 */
export function renew(): Promise<boolean> {
  if (!manager || authState.status === 'disabled') return Promise.resolve(false);
  refreshInFlight ??= (async () => {
    try {
      const current = authState.user ?? await manager!.getUser();
      if (!current?.refresh_token) throw new Error('no refresh token');
      const user = await manager!.signinSilent();
      if (!user) throw new Error('no user');
      const wasSignedIn = authState.status === 'signed-in';
      authState.user = user;
      if (!wasSignedIn) await adoptUser(user);
      return true;
    } catch {
      const wasSignedIn = authState.status === 'signed-in';
      await dropUser('expired');
      if (wasSignedIn) emit();
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * Перейти на страницу входа Authentik. Ошибка (Authentik не ответил за
 * AUTH_REQUEST_TIMEOUT_SECONDS, вход не настроен) пробрасывается вызывающему —
 * до того, как библиотека начнёт навигацию.
 */
export async function signIn(): Promise<void> {
  if (!manager) throw new Error(tr('вход не настроен на сервере'));
  // Discovery — до signinRedirect: при сбое библиотека не успевает вызвать
  // window.stop() и оборвать уже начатый переход.
  const metadata = await manager.metadataService.getMetadata();
  const returnTo = `${window.location.pathname}${window.location.search}`;
  await manager.signinRedirect({ state: { returnTo: safeReturnPath(returnTo) }, scope: supportedScopes(metadata.scopes_supported) });
}

/**
 * Запрашиваемые scope — только те, что провайдер объявил в discovery
 * (scopes_supported): лишний scope из OIDC_SCOPES бэкенда (например,
 * `groups`, которого у провайдера нет) не должен ломать вход.
 */
function supportedScopes(supported: string[] | undefined): string {
  const requested = manager!.settings.scope.split(' ').filter(Boolean);
  if (supported?.length) return requested.filter((scope) => scope === 'openid' || supported.includes(scope)).join(' ');
  return requested.join(' ');
}

export async function signOut(): Promise<void> {
  if (!manager) return;
  const user = authState.user;
  await dropUser('signed-out');
  try {
    // end_session Authentik с id_token_hint и возвратом на главную.
    await manager.signoutRedirect({ id_token_hint: user?.id_token, post_logout_redirect_uri: `${window.location.origin}/` });
  } catch {
    // end_session недоступен — локальный выход уже сделан.
    window.location.assign('/');
  }
}

// --- Заголовки запросов --------------------------------------------------------

function isGisUrl(url: string): boolean {
  let path: string;
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin) return false;
    path = parsed.pathname;
  } catch {
    return false;
  }
  return path.startsWith('/gis/v1/') || path.startsWith('/gis/tiles/');
}

function isApiUrl(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin && parsed.pathname.startsWith('/api/v1/');
  } catch {
    return false;
  }
}

/** Заголовки авторизации для своего /api/v1 и ГИС; чужим адресам — ничего. */
export function requestHeaders(url: string): Record<string, string> {
  if (authState.status === 'signed-in' && authState.user?.access_token) {
    return isApiUrl(url) || isGisUrl(url) ? { Authorization: `Bearer ${authState.user.access_token}` } : {};
  }
  if (authState.status === 'anonymous' && geoTicket && isGisUrl(url)) {
    return { [geoTicket.header]: geoTicket.value };
  }
  return {};
}

// --- Пропуск ГИС (аноним) -------------------------------------------------------

function clearGeoTicket(): void {
  geoTicket = null;
  geoTicketDashboardId = null;
  if (geoTicketTimer != null) window.clearTimeout(geoTicketTimer);
  geoTicketTimer = null;
}

/**
 * Пропуск к ГИС для анонимного просмотра общего дашборда. Вошедшему не
 * нужен — ГИС принимает его токен. Обновляется за минуту до истечения, пока
 * открыт тот же дашборд.
 */
export async function ensureGeoTicket(dashboardId: string | null): Promise<void> {
  if (authState.status !== 'anonymous' || !authState.config?.geo_ticket_enabled) {
    clearGeoTicket();
    return;
  }
  if (!dashboardId) {
    clearGeoTicket();
    return;
  }
  const now = Date.now();
  if (geoTicket && geoTicket.dashboardId === dashboardId && geoTicket.expiresAt - now > GEO_TICKET_REFRESH_MARGIN_SECONDS * 1000) {
    return;
  }
  geoTicketDashboardId = dashboardId;
  try {
    const issued: GeoTicket = await api.geoTicket(dashboardId);
    if (geoTicketDashboardId !== dashboardId || authState.status !== 'anonymous') return;
    const expiresAt = Date.parse(issued.expires_at) || now + issued.expires_in * 1000;
    geoTicket = { value: issued.ticket, header: issued.header || authState.config.geo_ticket_header, dashboardId, expiresAt };
    scheduleGeoTicket(dashboardId, Math.max(5_000, expiresAt - Date.now() - GEO_TICKET_REFRESH_MARGIN_SECONDS * 1000));
  } catch {
    // Без пропуска карты останутся без подложки ГИС; повтор — через минуту.
    scheduleGeoTicket(dashboardId, 60_000);
  }
}

function scheduleGeoTicket(dashboardId: string, delay: number): void {
  if (geoTicketTimer != null) window.clearTimeout(geoTicketTimer);
  geoTicketTimer = window.setTimeout(() => {
    geoTicketTimer = null;
    if (geoTicketDashboardId === dashboardId) {
      if (geoTicket) geoTicket.expiresAt = 0;
      void ensureGeoTicket(dashboardId);
    }
  }, delay);
}
